import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Modal } from 'antd';
import styled from 'styled-components';

import { getMostCommonCommunityContract } from 'apis/CommunityAPI';
import { isCommunityCCRC } from 'components/Community/utils';
import { DISPLAY_VALUES, ORIENTATION_TYPE } from 'constants/charts';
import { localizeAreaLabel } from 'utils/helpers/units';

import CommunityViewWidgetModalContent from './CommunityViewWidgetModalContent';
import { CommunityViewWidgetModalContext } from './CommunityViewWidgetModalContext';
import CommunityViewWidgetModalFilters from './CommunityViewWidgetModalFilters';
import CommunityViewWidgetModalFooter from './CommunityViewWidgetModalFooter';
import CommunityViewWidgetModalInfo from './CommunityViewWidgetModalInfo';
import { WidgetGrouping, WidgetType } from '../../../common/Widgets/types';
import { getWidgetTypeLabel } from '../../../common/Widgets/utils';
import { DATA_DISPLAY_TYPE, DATA_RANGE_TYPE, PRICE_DISPLAY_TYPE } from '../../../common/Widgets/Widget/constants';
import { useCommunityViewContext } from '../CommunityViewContext';
import { AGGREGATION_TYPE, CREATE_WIDGET_MODAL_WIDTH } from '../constants';

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 0px;
    padding: 0;
  }
  .ant-modal-body {
    line-height: 1;
  }
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 614px;
  box-sizing: border-box;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

interface Props {
  open: boolean;
  modalType: string;
  onClose: any;
  widget?: WidgetType;
}

const CommunityViewWidgetModal = ({ open, modalType, onClose, widget }: Props) => {
  const { community, mostCommonCommunityContract } = useCommunityViewContext();

  const [id, setId] = useState<number | undefined>(undefined);
  const [type, setType] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const [filterId, setFilterId] = useState<number | undefined>(undefined);
  const [competitors, setCompetitors] = useState<number[]>([]);
  const [careTypes, setCareTypes] = useState<number[]>([]);
  const [roomTypes, setRoomTypes] = useState<number[]>([]);
  const [roomTypesPriceDisplay, setRoomTypesPriceDisplay] = useState<number>(AGGREGATION_TYPE.AVG);
  const [careLevels, setCareLevels] = useState<number[]>([]);
  const [careLevelsStatsType, setCareLevelsStatsType] = useState<number>(AGGREGATION_TYPE.AVG);
  const [medicationLevels, setMedicationLevels] = useState<number[]>([]);
  const [medicationLevelsStatsType, setMedicationLevelsStatsType] = useState<number>(AGGREGATION_TYPE.AVG);
  const [reviewSources, setReviewSources] = useState<number[]>([]);
  const [reviewCategories, setReviewCategories] = useState<number[]>([]);
  const [swotCategories, setSwotCategories] = useState<number[]>([]);
  const [amenityCategories, setAmenityCategories] = useState<number[]>([]);
  const [contractTypes, setContractTypes] = useState<number[]>([]);
  const [communityContractType, setCommunityContractType] = useState<number | undefined>();
  const [communityContractSelectionType, setCommunityContractSelectionType] = useState<number | undefined>();
  const [initialRange, setInitialRange] = useState<string>(DATA_RANGE_TYPE.NOW);
  const [initialFormat, setInitialFormat] = useState<string>(DATA_DISPLAY_TYPE.GRAPH);
  const [initialOrientation, setInitialOrientation] = useState<string>(ORIENTATION_TYPE.HORIZONTAL);
  const [displayValue, setDisplayValue] = useState<number>(DISPLAY_VALUES.DISPLAY.value);
  const [priceDisplay, setPriceDisplay] = useState<number>(PRICE_DISPLAY_TYPE.SINGLE);
  const [groupBy, setGroupBy] = useState<WidgetGrouping | undefined>(undefined);

  useEffect(() => {
    if (widget) {
      setId(widget?.id);
      setType(widget.type);
      setName(localizeAreaLabel(widget.name || getWidgetTypeLabel(widget.type, community), community));
      setNote(widget.note);

      setFilterId(widget.filters.id);
      setCompetitors(widget.filters.competitors);
      setCareTypes(widget.filters.careTypes);
      setRoomTypes(widget.filters.roomTypes);
      setRoomTypesPriceDisplay(widget.filters.roomTypesPriceDisplay || AGGREGATION_TYPE.AVG);
      setCareLevels(widget.filters.careLevels);
      setCareLevelsStatsType(widget.filters.careLevelsStatsType || AGGREGATION_TYPE.AVG);
      setMedicationLevels(widget.filters.medicationLevels || []);
      setMedicationLevelsStatsType(widget.filters.medicationLevelsStatsType || AGGREGATION_TYPE.AVG);
      setReviewSources(widget.filters.reviewSources);
      setReviewCategories(widget.filters.reviewCategories);
      setSwotCategories(widget.filters.swotCategories);
      if (widget.filters.amenityCategories?.length) {
        setAmenityCategories(widget.filters.amenityCategories);
      }
      if (widget.filters.contractTypes?.length) {
        setContractTypes(widget.filters.contractTypes);
      } else {
        setContractTypes([]);
      }
      if (widget.initial_settings?.range) {
        setInitialRange(widget.initial_settings?.range);
      }
      if (widget.initial_settings?.format) {
        setInitialFormat(widget.initial_settings?.format);
      }
      if (widget.initial_settings?.orientation) {
        setInitialOrientation(widget.initial_settings?.orientation);
      }
      if (widget.initial_settings?.display_value !== undefined && widget.initial_settings?.display_value !== null) {
        setDisplayValue(widget.initial_settings?.display_value);
      }
      if (widget.initial_settings?.price_display) {
        setPriceDisplay(widget.initial_settings?.price_display);
      }
      if (widget.filters.communityContractSelectionType) {
        setCommunityContractSelectionType(widget.filters.communityContractSelectionType);
      }
      if (widget.group_by) {
        setGroupBy(widget.group_by);
      }

      if (widget.filters.communityContractType) {
        setCommunityContractType(widget.filters.communityContractType);
        if (widget.filters.contractTypes?.length) {
          setContractTypes(widget.filters.contractTypes);
        } else if (community?.contracts?.length) {
          const match = community.contracts.find(
            (contract: any) => contract.id === widget.filters.communityContractType
          );
          if (match?.contract_id) {
            setContractTypes([match.contract_id]);
          }
        }
      } else if (community?.contracts?.length && mostCommonCommunityContract) {
        setCommunityContractType(mostCommonCommunityContract.id);
        if (mostCommonCommunityContract.contract_id) {
          setContractTypes([mostCommonCommunityContract.contract_id]);
        }
      } else if (community?.contracts?.length > 0) {
        setCommunityContractType(community.contracts[0].id);
      }
    }
  }, [widget, community, mostCommonCommunityContract]);

  return (
    <CommunityViewWidgetModalContext.Provider
      value={{
        originalWidget: widget,
        id,
        name,
        note,
        type,
        filterId,
        competitors,
        careTypes,
        roomTypes,
        roomTypesPriceDisplay,
        careLevels,
        medicationLevels,
        reviewSources,
        reviewCategories,
        swotCategories,
        careLevelsStatsType,
        medicationLevelsStatsType,
        amenityCategories,
        contractTypes,
        communityContractType,
        communityContractSelectionType,
        priceDisplay,
        initialRange,
        initialFormat,
        initialOrientation,
        displayValue,
        groupBy,
        setName,
        setNote,
        setCompetitors,
        setCareTypes,
        setRoomTypes,
        setRoomTypesPriceDisplay,
        setCareLevels,
        setMedicationLevels,
        setReviewSources,
        setReviewCategories,
        setSwotCategories,
        setCareLevelsStatsType,
        setMedicationLevelsStatsType,
        setAmenityCategories,
        setContractTypes,
        setCommunityContractType,
        setCommunityContractSelectionType,
        setPriceDisplay,
        setInitialRange,
        setInitialFormat,
        setInitialOrientation,
        setDisplayValue,
        setGroupBy,
        modalType,
        onClose,
      }}
    >
      <StyledModal open={open} footer={false} closable={false} destroyOnClose width={CREATE_WIDGET_MODAL_WIDTH}>
        <Container>
          <CommunityViewWidgetModalFilters />
          <Content>
            <CommunityViewWidgetModalInfo />
            <CommunityViewWidgetModalContent />
            <CommunityViewWidgetModalFooter />
          </Content>
        </Container>
      </StyledModal>
    </CommunityViewWidgetModalContext.Provider>
  );
};

export default CommunityViewWidgetModal;
